import React, { useState } from 'react';

import { getUncheckedItemsCount } from '@libs/NwChecklists';
import { getContactSubTypeName } from '@libs/NwCustomerTypes';
import { getVisibilityNotes } from '@libs/NwNotes';

import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import ContactDetailsCustomProperties from '@components/ContactDetails/CommonComponents/CustomProperties/ContactDetailsCustomProperties';

import CheckList from '@components/CheckLists/CheckList';
import { CheckListProgress } from '@components/CheckLists/CheckListElements';
import ContactDetailsNotes from '@components/ContactDetailsNotes/ContactDetailsNotes';
import { NwAddButton, NwEditButton } from '@components/Gui/NwButton';
import { NwCardTabs } from '@components/Gui/NwCard/NwCardTabs';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CustomerDetailsCustomPropertiesAndNotesTabs = ({ currentContact, drawerMode, ...rest }) => {
    const [{ areaContext }] = useStateValue();

    const [createNotes, setCreateNotes] = useState(false);
    const [editCustomProperties, setEditCustomProperties] = useState(false);

    const viewAgencyNotes = useUserPermission(
        'ViewAgencyNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewClientNotes = useUserPermission(
        'ViewClientNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewServiceNotes = useUserPermission(
        'ViewServiceNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const afterNotesCreation = () => {
        setCreateNotes(false);
    };
    const afterEditingProperties = () => {
        setEditCustomProperties(false);
    };

    const checkListStatus = getUncheckedItemsCount(currentContact.CheckListItems);

    const [activeTab, setActiveTab] = useState(checkListStatus.complete ? 'customproperties' : 'checklist');

    const contactSubTypeName = getContactSubTypeName(currentContact.SubType);

    const viewPolicy = () => {
        if (contactSubTypeName === 'Agency') {
            return viewAgencyNotes;
        } else if (contactSubTypeName === 'Client') {
            return viewClientNotes;
        }
        return viewServiceNotes;
    };

    const tabs = [
        {
            key: 'customproperties',
            icon: byPrefixAndName.fasl['circle-info'],
            name: 'Additional Info',
        },
        {
            key: 'notes',
            icon: byPrefixAndName.fasl['sticky-note'],
            name: 'Notes',
            counter: viewPolicy() ? getVisibilityNotes(currentContact.Notes, areaContext.area).length : null,
        },
    ];
    if (checkListStatus.total > 0) {
        tabs.push({
            key: 'checklist',
            name: 'Checklist',
            prefix: [<CheckListProgress key="checklist-progress" checkListStatus={checkListStatus} />],
        });
    }

    const renderTabs = () => {
        switch (activeTab) {
            case 'notes':
                return (
                    <ContactDetailsNotes
                        isInCardTab
                        currentContact={currentContact}
                        drawerMode={drawerMode}
                        createNotes={createNotes}
                        afterNotesCreation={afterNotesCreation}
                        noPagination
                    />
                );
            case 'checklist':
                return (
                    <CheckList
                        objectID={currentContact.ID}
                        objectType={contactSubTypeName}
                        checkListItems={currentContact.CheckListItems}
                    />
                );
            default:
                return (
                    <ContactDetailsCustomProperties
                        currentContact={currentContact}
                        contactType={contactSubTypeName}
                        fullHeight
                        isInCardTab
                        editMode={editCustomProperties}
                        afterEditingProperties={afterEditingProperties}
                    />
                );
        }
    };

    const getTabsExtraContent = () => {
        switch (activeTab) {
            case 'notes':
                return (
                    <NwAddButton
                        ghost
                        disabled={!viewPolicy()}
                        size="small"
                        onClick={() => {
                            setCreateNotes(true);
                        }}
                        label="add new"
                    />
                );
            case 'customproperties':
                return <NwEditButton ghost onClick={() => setEditCustomProperties(true)} />;
            default:
                return null;
        }
    };

    const onTabChange = (key) => {
        setActiveTab(key);
    };

    return currentContact ? (
        <NwCardTabs
            fullheight
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            tabBarExtraContent={getTabsExtraContent()}
            {...rest}
        >
            {renderTabs()}
        </NwCardTabs>
    ) : (
        <div />
    );
};

export default CustomerDetailsCustomPropertiesAndNotesTabs;
